<template>
  <Modal v-bind="$attrs" v-on="$listeners" @closed="closed">
    <template #title>お客様宛メール テンプレート・追加文</template>
    <div class="main-height main-width flex flex-col">
      <!-- 種類のタブ -->
      <div class="flex items-end border-b border-gray-200 mb-2">
        <button
          v-for="k in kinds"
          :key="k.key"
          type="button"
          class="px-5 py-2 text-sm font-bold border-b-4"
          :class="kind == k.key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'"
          @click="changeKind(k.key)"
        >
          {{ k.label }}
        </button>
        <div class="ml-4 mb-2 text-xs text-gray-600">{{ kindNote }}</div>
        <!-- 他の人のテンプレートを見る／自分のに戻る -->
        <button
          type="button"
          class="ml-auto mb-2 px-3 py-1 rounded text-xs font-bold border flex-shrink-0"
          :class="othersMode ? 'bg-white border-indigo-600 text-indigo-700 hover:bg-indigo-50' : 'bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-500'"
          v-tooltip="othersMode ? '' : '他の人のテンプレート・追加文を見ます'"
          @click="toggleOthers()"
        >
          {{ othersMode ? '自分のに戻る' : '他人のを確認' }}
        </button>
      </div>
      <div class="text-xs mb-3 text-green-700 font-bold">
        {{ othersMode ? othersEditRule : standardEditRule }}
      </div>

      <div class="flex flex-1 min-h-0">
        <!-- 他人のを確認：左端に登録している人の一覧 -->
        <div v-if="othersMode" class="w-44 flex-shrink-0 border-r border-gray-200 px-2 mr-3 overflow-auto">
          <div class="py-3 mb-2 text-xs font-bold text-gray-700">登録している人</div>
          <div
            v-for="o in owners"
            :key="o.userId"
            class="px-2 py-2 rounded border mb-1 text-sm cursor-pointer"
            :class="owner && owner.userId == o.userId ? 'bg-indigo-100 border-indigo-400 font-bold' : 'border-gray-200 hover:bg-gray-100'"
            @click="selectOwner(o)"
          >
            <div class="break-all">{{ o.name }}</div>
          </div>
          <p v-if="!owners.length" class="text-xs text-gray-500 px-2">他の人はまだ登録していません。</p>
        </div>

        <!-- 一覧 -->
        <div class="w-64 flex-shrink-0 border-r border-gray-200 px-3 mr-3 overflow-auto">
          <div class="py-3 mb-2">
            <PrimaryButton v-if="!othersMode" text="＋ 新規作成" size="sm" class="w-full" @click="addNew()" />
            <div v-else class="text-xs font-bold text-gray-700" :class="owner ? '' : 'invisible'">{{ owner ? owner.name + ' さんの' + kindLabel() : '-' }}</div>
          </div>
          <div
            v-for="t in listByKind"
            :key="t.id || 'new'"
            class="flex items-center px-2 py-2 rounded border mb-1 text-sm"
            :class="val && val.id == t.id ? 'bg-indigo-100 border-indigo-400' : 'border-gray-200 hover:bg-gray-100'"
          >
            <div class="flex-1 min-w-0 cursor-pointer" @click="select(t)">
              <div class="break-all" :class="val && val.id == t.id ? 'font-bold' : ''">{{ t.name }}</div>
              <div class="text-xs">
                <span v-if="!t.id" class="text-red-700">未保存</span>
                <span v-else-if="!t.userId" class="inline-block px-1.5 rounded bg-green-600 text-white font-bold">共通用</span>
                <span v-else-if="t.isDefault" class="text-green-700">デフォルト</span>
              </div>
            </div>
            <button
              v-if="t.id"
              type="button"
              class="ml-2 px-2 py-1 rounded text-xs font-bold bg-white border border-indigo-600 text-indigo-700 hover:bg-indigo-50 flex-shrink-0"
              v-tooltip="'この内容を写して、自分用を新しく作ります'"
              @click.stop="duplicate(t)"
            >
              自分用に複製
            </button>
          </div>
          <p v-if="!listByKind.length && emptyNote" class="text-xs text-gray-500 px-2">{{ emptyNote }}</p>
        </div>

        <!-- 右：編集 -->
        <div v-if="val" class="flex-1 flex flex-col min-w-0 overflow-auto px-2">
          <div v-if="!editable" class="text-xs text-red-700 font-bold mb-2">
            権限がないため{{ val.userId ? '' : '「共通用」を' }}編集できません。左の「自分用に複製」で自分用を作ってください。
          </div>

          <div class="flex items-end mb-1">
            <div class="text-xs font-bold text-gray-700">タイトル</div>
            <!-- 削除は保存と離して右上に置く -->
            <PrimaryButton v-if="canDelete" text="削除" size="red-color text-white text-xs font-bold rounded px-3 py-1" class="ml-auto" @click="remove()" />
          </div>
          <input
            type="text"
            class="border-gray-300 rounded w-full py-1 text-sm focus:ring-indigo-500 focus:border-indigo-500 mb-3"
            :class="!editable ? 'bg-gray-100 text-gray-500' : ''"
            :disabled="!editable"
            v-model="val.name"
          />

          <div class="text-xs font-bold text-gray-700">本文</div>
          <div class="text-xs text-blue-700 font-bold mb-1">＊＊文中の差し込みたいところで、こちらのボタンを押してください＊＊</div>
          <div class="flex flex-wrap gap-1 mb-1">
            <button
              v-for="p in placeholders"
              :key="p.key"
              type="button"
              class="px-2 py-1 rounded text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed"
              :disabled="!editable"
              v-tooltip="p.note"
              @mousedown.prevent
              @click="insertMark(p.key)"
            >
              {{ p.key }}
            </button>
          </div>
          <textarea
            ref="body"
            class="border-gray-300 rounded w-full text-sm focus:ring-indigo-500 focus:border-indigo-500"
            :class="!editable ? 'bg-gray-100 text-gray-500' : ''"
            :style="{ 'field-sizing': 'content', 'min-height': kind == kindTemplate ? '320px' : '120px' }"
            :disabled="!editable"
            v-model="val.body"
          />

          <!-- デフォルトのチェック。他人のものは見せるだけ（持ち主の好みなので変えない） -->
          <div v-if="kind == kindTemplate && val.userId" class="flex items-center mt-5 mx-3">
            <input type="checkbox" id="mailTemplateDefault" class="rounded text-indigo-600" :disabled="!isMine" v-model="val.isDefault" />
            <label for="mailTemplateDefault" class="ml-2 text-sm text-gray-700">
              {{ isMine ? '自分のデフォルトにする' : ownerName + ' さんのデフォルト' }}
            </label>
          </div>

          <div class="flex mt-6 mb-4 mx-3">
            <PrimaryButton text="閉じる" size="inline-flex items-center px-6 py-2 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white border border-gray-300 hover:bg-gray-50" class="ml-auto" @click="close()" />
            <PrimaryButton v-if="editable" text="保存" size="normal px-10" class="ml-3" @click="save()" />
          </div>
        </div>
        <div v-else class="flex-1 flex items-center justify-center text-gray-400 font-bold text-sm">
          <template v-if="!othersMode">左から選ぶか、「新規作成」を押してください</template>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from '@components/Modal.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import * as mailTemplateManager from '@managers/mailTemplateManager'
import * as dialogs from '@libs/dialogs'
import * as logManager from '@managers/logManager'
import * as utils from '@libs/utils'

export default {
  components: {
    Modal,
    PrimaryButton
  },

  props: ['open'],

  data() {
    return {
      // 一覧（テンプレートも追加文も全部）
      list: [],
      // 開いている種類（テンプレート／追加文）
      kind: mailTemplateManager.KIND_TEMPLATE,
      kinds: mailTemplateManager.KINDS,
      kindTemplate: mailTemplateManager.KIND_TEMPLATE,
      // 編集中のデータ
      val: null,
      // 選んだときの内容（変更の有無を見る）
      original: '',
      // 印のボタン
      placeholders: mailTemplateManager.PLACEHOLDERS,
      standardEditRule: mailTemplateManager.STANDARD_EDIT_RULE,
      othersEditRule: mailTemplateManager.OTHERS_EDIT_RULE,
      // 他人のを確認しているか
      othersMode: false,
      // 登録している人の一覧（他人のを確認するとき）
      owners: [],
      // 一覧で選んだ人
      owner: null
    }
  },

  computed: {
    /**
     * ログインユーザーID
     */
    userId() {
      return this.$store.getters.user.id
    },

    /**
     * 共通用を編集できる人か
     */
    canEditStandard() {
      return mailTemplateManager.canEditStandard(this.$store)
    },

    /**
     * 開いている種類の説明
     */
    kindNote() {
      const k = this.kinds.find((k) => k.key == this.kind)
      return k ? k.note : ''
    },

    /**
     * 開いている種類の一覧
     */
    listByKind() {
      return this.list.filter((t) => t.kind == this.kind)
    },

    /**
     * 一覧を取る持ち主のユーザーID（他人のを確認しているときはその人）
     */
    ownerId() {
      return this.othersMode && this.owner ? this.owner.userId : this.userId
    },

    /**
     * 編集中のものの持ち主の名前（他人のを確認しているとき）
     */
    ownerName() {
      return this.othersMode && this.owner ? this.owner.name : ''
    },

    /**
     * 編集中のものが自分のものか
     */
    isMine() {
      return !!this.val && !!this.val.userId && this.val.userId == this.userId
    },

    /**
     * 編集できるか（自分のもの、または共通用・他人のものを編集できる権限者）
     */
    editable() {
      if (!this.val) {
        return false
      }
      return this.isMine || this.canEditStandard
    },

    /**
     * 削除できるか（自分のものは本人、共通用・他人のものは権限者。共通用の標準テンプレートは 1 つ残すので消せない）
     */
    canDelete() {
      if (!this.val || !this.val.id) {
        return false
      }
      if (this.isMine) {
        return true
      }
      if (!this.canEditStandard) {
        return false
      }
      if (this.val.userId) {
        return true
      }
      // 共通用のテンプレートは最後の 1 つを消せない
      if (this.kind == this.kindTemplate) {
        return this.list.filter((t) => t.kind == this.kindTemplate && !t.userId && t.id).length > 1
      }
      return true
    },

    /**
     * 編集中の内容が変わっているか
     */
    isDirty() {
      return !!this.val && JSON.stringify(this.val) !== this.original
    },

    /**
     * 一覧が空のときの案内（他人のを確認していて、まだ人を選んでいないときだけ）
     */
    emptyNote() {
      return this.othersMode && !this.owner ? '確認したい人をクリックしてください' : ''
    }
  },

  watch: {
    open() {
      if (this.open) {
        this.getData()
      }
    }
  },

  methods: {
    /**
     * 一覧取得
     * @param selectId 取得後に選ぶ id
     */
    async getData(selectId) {
      // 他人のを確認しているときは、人を選ぶまで一覧は空。選んだら共通用を除いてその人のものだけ
      if (this.othersMode && !this.owner) {
        this.list = []
        this.setVal(null)
        return
      }
      this.list = await mailTemplateManager.getList(this.ownerId, this.othersMode)
      let tar = null
      if (selectId) {
        tar = this.listByKind.find((t) => t.id == selectId)
      } else if (this.val && this.val.id) {
        tar = this.listByKind.find((t) => t.id == this.val.id)
      }
      if (!tar) {
        tar = this.kind == this.kindTemplate ? mailTemplateManager.getInitialTemplate(this.list) : this.listByKind[0]
      }
      this.setVal(tar)
    },

    /**
     * 編集対象にする
     */
    setVal(t) {
      this.val = t ? utils.clone(t) : null
      this.original = this.val ? JSON.stringify(this.val) : ''
    },

    /**
     * 変更を捨ててよいか確認
     */
    async confirmDiscard() {
      if (!this.isDirty) {
        return true
      }
      const res = await dialogs.showWarningConfirmDialog('保存していません', '編集中の内容を保存していません。破棄してよろしいですか？', '破棄', '戻る')
      return res == 'YES'
    },

    /**
     * 未保存のものを一覧から消す
     */
    dropUnsaved() {
      this.list = this.list.filter((l) => l.id)
    },

    /**
     * 「他人のを確認」と「自分のに戻る」の切り替え
     */
    async toggleOthers() {
      if (!await this.confirmDiscard()) {
        return
      }
      this.dropUnsaved()
      this.owner = null
      if (this.othersMode) {
        this.othersMode = false
        this.owners = []
        await this.getData()
      } else {
        this.othersMode = true
        this.list = []
        this.setVal(null)
        this.owners = await mailTemplateManager.getOwnerList(this.userId)
      }
    },

    /**
     * 登録している人の一覧で選択
     * @param o 人
     */
    async selectOwner(o) {
      if (this.owner && this.owner.userId == o.userId) {
        return
      }
      if (!await this.confirmDiscard()) {
        return
      }
      this.owner = o
      this.setVal(null)
      await this.getData()
    },

    /**
     * 種類のタブを切り替え
     */
    async changeKind(k) {
      if (this.kind == k) {
        return
      }
      if (!await this.confirmDiscard()) {
        return
      }
      this.dropUnsaved()
      this.kind = k
      const tar = k == this.kindTemplate ? mailTemplateManager.getInitialTemplate(this.list) : this.listByKind[0]
      this.setVal(tar)
    },

    /**
     * 一覧で選択
     */
    async select(t) {
      if (this.val && this.val.id == t.id) {
        return
      }
      if (!await this.confirmDiscard()) {
        return
      }
      this.dropUnsaved()
      this.setVal(t)
    },

    /**
     * 未保存の新しいデータを一覧に足して開く
     * @param name 名前
     * @param body 本文
     */
    async openNew(name, body) {
      if (!await this.confirmDiscard()) {
        return
      }
      this.dropUnsaved()
      const t = {
        id: null,
        kind: this.kind,
        userId: this.userId,
        name,
        body,
        isDefault: false
      }
      this.list.push(t)
      this.val = t
      // 新規は保存前でも「変更あり」扱いにしない
      this.original = JSON.stringify(t)
    },

    /**
     * 空から新しく作る
     */
    addNew() {
      const name = this.kind == this.kindTemplate ? '新しいテンプレート' : '新しい追加文'
      this.openNew(name, '')
    },

    /**
     * その内容を写して自分用を新しく作る
     * 他人のを確認しているときは、自分の画面に戻ってから開く
     * @param t 元にするデータ
     */
    async duplicate(t) {
      if (this.othersMode) {
        if (!await this.confirmDiscard()) {
          return
        }
        this.othersMode = false
        this.owner = null
        this.owners = []
        await this.getData()
      }
      this.openNew(t.name + 'の複製', t.body)
    },

    /**
     * 本文の今の位置に印を入れる
     * @param key 印のキー
     */
    insertMark(key) {
      // ボタンは mousedown.prevent で本文欄からフォーカスを奪わないので、カーソル位置はそのまま読める
      // 位置は本文欄が持つ文字列（改行は \n）を基準に数えるので、同じ文字列を元にする（データ側は \r\n のことがある）
      const el = this.$refs.body
      const m = mailTemplateManager.mark(key)
      const body = el ? el.value : (this.val.body || '')
      const start = el ? el.selectionStart : body.length
      const end = el ? el.selectionEnd : body.length
      this.val.body = body.slice(0, start) + m + body.slice(end)
      const pos = start + m.length
      this.$nextTick(() => {
        if (el) {
          // 値の差し替えでカーソルが末尾に動くので戻す。画面が下に飛ばないようにスクロールはさせない
          el.focus({ preventScroll: true })
          el.setSelectionRange(pos, pos)
        }
      })
    },

    /**
     * 入力チェック
     */
    async validate() {
      if (!this.val.name || !this.val.name.trim()) {
        await dialogs.showErrorDialog('タイトル', 'タイトルを入力してください。')
        return false
      }
      if (!this.val.body || !this.val.body.trim()) {
        await dialogs.showErrorDialog('本文', '本文を入力してください。')
        return false
      }
      const unknown = mailTemplateManager.findUnknownMarks(this.val.body)
      if (unknown.length) {
        const res = await dialogs.showWarningConfirmDialog('置き換えられない印があります', unknown.join('、') + ' は置き換えの対象ではないので、そのままメールに出ます。このまま保存しますか？', '保存する', '戻る')
        if (res != 'YES') {
          return false
        }
      }
      return true
    },

    /**
     * 保存
     */
    async save() {
      if (!await this.validate()) {
        return
      }
      const param = {
        id: this.val.id,
        kind: this.val.kind,
        userId: this.val.userId,
        name: this.val.name.trim(),
        body: this.val.body,
        isDefault: !!this.val.isDefault,
        loginUserId: this.userId
      }
      const id = await mailTemplateManager.save(param)
      logManager.recording(this, logManager.Loglevel.INFO, 'メール本文テンプレート', '保存', `${this.ownerLabel()}${this.kindLabel()}「${param.name}」を保存しました。`)
      await this.getData(id || this.val.id)
      this.$emit('saved')
      await dialogs.showSuccessDialog('保存完了', `「${param.name}」を保存しました。`)
    },

    /**
     * 削除
     */
    async remove() {
      const res = await dialogs.showWarningConfirmDialog('削除', `「${this.val.name}」を削除してよろしいですか？`)
      if (res != 'YES') {
        return
      }
      await mailTemplateManager.remove(this.val.id, this.userId, this.canEditStandard)
      logManager.recording(this, logManager.Loglevel.INFO, 'メール本文テンプレート', '削除', `${this.ownerLabel()}${this.kindLabel()}「${this.val.name}」を削除しました。`)
      this.val = null
      if (this.othersMode) {
        // 件数が変わる。全部消えた人は一覧から外れる
        this.owners = await mailTemplateManager.getOwnerList(this.userId)
        this.owner = this.owners.find((o) => o.userId == this.owner.userId) || null
      }
      await this.getData()
      this.$emit('saved')
    },

    /**
     * 開いている種類の名前（ログ用）
     */
    kindLabel() {
      const k = this.kinds.find((k) => k.key == this.kind)
      return k ? k.label : ''
    },

    /**
     * 他人のものを直したときの持ち主の名前（ログ用）
     */
    ownerLabel() {
      return this.ownerName ? `${this.ownerName} さんの` : ''
    },

    /**
     * 閉じるボタン（保存していなければ確認してから）
     */
    async close() {
      if (!await this.confirmDiscard()) {
        return
      }
      this.$emit('input', false)
    },

    /**
     * 閉じたあとの片付け
     */
    closed() {
      this.$emit('close')
      this.list = []
      this.val = null
      this.original = ''
      this.kind = this.kindTemplate
      this.othersMode = false
      this.owners = []
      this.owner = null
    }
  }
}
</script>

<style scoped>
.main-height {
  height: 80vh;
}

.main-width {
  width: 85vw;
  max-width: 1100px;
}

.red-color {
  background: #c94242;
}
</style>
